// "Ask the room" (ADR-0035): POST /rooms/:roomId/ask. A question about what the
// room holds is answered with the documents that match — never with prose. One
// endpoint for every surface (admin, buyer room, seller submit); the API scopes
// the answer to what the caller may see, exactly like the listing and search.
import { categoryPathLabel } from '../lib/period-tree';
import { api } from './client';
import type { RoomDocument } from './types';

/** Longest question the endpoint accepts (server ASK_MAX_QUESTION_CHARS). */
export const ASK_MAX_QUESTION_CHARS = 300;

/** The documents found for a question. */
export interface AskDocumentsAnswer {
  kind: 'documents';
  /** Document-listing rows, so they open in the existing viewer. */
  results: RoomDocument[];
  /** More documents matched than were listed. */
  truncated: boolean;
  /** The folder the question was matched to (one the caller can see), or null when the answer came from keyword search. */
  category: { id: string; path: string[] } | null;
  /** Years the question named ([] = none). */
  years: number[];
  /** Only the latest period per kind of document was kept. */
  latestOnly: boolean;
}

/** Any question that is not a request to find documents: one fixed message from the server. */
export interface AskNotAFindAnswer {
  kind: 'not_a_find_request';
  message: string;
}

export type AskRoomAnswer = AskDocumentsAnswer | AskNotAFindAnswer;

/** Ask a room a question. The question travels in the body, never the URL. */
export function askRoom(roomId: string, question: string): Promise<AskRoomAnswer> {
  return api.post<AskRoomAnswer>(`/rooms/${roomId}/ask`, { question });
}

/** One plain line describing how a documents answer was narrowed, e.g.
 * "Financial Performance › Profit and Loss Statements · 2023 · latest only". */
export function describeAskAnswer(answer: AskDocumentsAnswer): string {
  const parts: string[] = [];
  if (answer.category) parts.push(categoryPathLabel(answer.category.path, ' › '));
  else parts.push('Matching names and text');
  if (answer.years.length > 0) parts.push(answer.years.join(', '));
  if (answer.latestOnly) parts.push('latest only');
  return parts.join(' · ');
}
